import { useRef } from "react";
import { cn } from "../../lib/utils.ts";

const STEP: Record<string, (i: number, n: number) => number> = {
  ArrowRight: (i, n) => (i + 1) % n,
  ArrowLeft: (i, n) => (i - 1 + n) % n,
  Home: () => 0,
  End: (_, n) => n - 1,
};

/**
 * The overview's chart switcher, styled like the log's baby tabs at a
 * section's size: ink on paper for the active tab, faded for the rest. One
 * tab needs no switcher, so it renders nothing. The arrow keys, Home, and End
 * move between tabs; Tab moves on to the chart.
 */
export function ChartTabs<K extends string>({
  tabs,
  selected,
  onSelect,
  panelId,
  label,
}: {
  tabs: readonly { key: K; label: string }[];
  selected: K;
  onSelect: (key: K) => void;
  /** The id of the tab panel the tabs control. */
  panelId: string;
  label: string;
}) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  if (tabs.length < 2) return null;
  return (
    <div
      role="tablist"
      aria-label={label}
      className="mt-4 grid border border-divider"
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      {tabs.map((t, i) => {
        const active = t.key === selected;
        return (
          <button
            key={t.key}
            ref={(el) => {
              buttons.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${panelId}-${t.key}`}
            aria-selected={active}
            aria-controls={panelId}
            tabIndex={active ? 0 : -1}
            onClick={() => onSelect(t.key)}
            onKeyDown={(e) => {
              const step = STEP[e.key];
              if (!step) return;
              e.preventDefault();
              const next = step(i, tabs.length);
              onSelect(tabs[next]!.key);
              buttons.current[next]?.focus();
            }}
            className={cn(
              "heading flex min-h-[44px] min-w-0 items-center justify-center px-2 text-[16px] transition-colors",
              active ? "bg-ink text-paper" : "bg-transparent text-ink opacity-55 hover:opacity-80",
            )}
          >
            <span className="min-w-0 truncate">{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}
